import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';
import { gameInstanceIndexSchema, machineIdSchema } from '../types/gameInstance';

/**
 * Access Policy namespace — share-code session establishment.
 *
 * Subset scope: this module covers the session-establishment core needed for
 * share-code-direct join. The full Access Policy surface (time windows,
 * schedules, allowedUsers, authRequirement, input permissions, content
 * gating, delegation, monetization) is defined in
 * docs/architecture/access-policy.md and extends this schema when those
 * features ship.
 *
 * Contract frozen by docs/feature-plans/game-instance-v2/PHASE_1.md.
 */

/**
 * An access policy grants join rights to a machine (or one game instance on
 * it) via a share code.
 */
export const accessPolicySchema = z.object({
  policyId: z.string(),
  machineId: machineIdSchema,
  gameInstanceIndex: gameInstanceIndexSchema.optional(), // absent = whole-machine policy (legacy-compatible)
  shareCode: z.string(),
  maxConcurrentUsers: z.number().int().min(1),
  createdBy: z.string(),
  expiresAt: z.number().optional(),
  revoked: z.boolean().default(false),
});
export type AccessPolicy = z.infer<typeof accessPolicySchema>;

/**
 * Player-facing redacted view of an access policy (contract amendment,
 * game-instance-v2 Phase 8 follow-up): `ACCESS_POLICY_RESOLVED` never exposes
 * `machineId`, `gameInstanceIndex`, `createdBy`, or `revoked` to players.
 * Instance identity travels only inside the signed contract, which the player
 * can present but not read.
 */
export const redactedAccessPolicySchema = accessPolicySchema.pick({
  policyId: true,
  shareCode: true,
  maxConcurrentUsers: true,
  expiresAt: true,
});
export type RedactedAccessPolicy = z.infer<typeof redactedAccessPolicySchema>;

/** Build the player-facing redacted view of a persisted policy. */
export function redactAccessPolicy(policy: AccessPolicy): RedactedAccessPolicy {
  return redactedAccessPolicySchema.parse({
    policyId: policy.policyId,
    shareCode: policy.shareCode,
    maxConcurrentUsers: policy.maxConcurrentUsers,
    expiresAt: policy.expiresAt,
  });
}

/**
 * PlayerSessionContract — signed by the API using ECDSA P-256 / JWS compact
 * serialization (ES256, per access-policy.md §5.1). Instance identity is
 * INSIDE the signed payload — an unsigned instance field would let a relay
 * re-target a contract to a different instance.
 */
export const playerSessionContractSchema = z.object({
  policyId: z.string(),
  machineId: machineIdSchema,
  gameInstanceIndex: gameInstanceIndexSchema.optional(),
  playerId: z.string(),
  playerPubKey: z.string().optional(),
  issuedAt: z.number(),
  expiresAt: z.number().optional(),
});
export type PlayerSessionContract = z.infer<typeof playerSessionContractSchema>;

/**
 * JWS compact serialization: base64url(header).base64url(payload).base64url(signature).
 * Verify with Playcast's public key.
 */
export const signedContractSchema = z.object({
  contract: playerSessionContractSchema,
  signature: z.string(),
});
export type SignedContract = z.infer<typeof signedContractSchema>;

export enum AccessPolicyMessageType {
  ACCESS_POLICY_CREATE = 'ACCESS_POLICY_CREATE',
  ACCESS_POLICY_CREATED = 'ACCESS_POLICY_CREATED',
  ACCESS_POLICY_REVOKE = 'ACCESS_POLICY_REVOKE',
  ACCESS_POLICY_REVOKED = 'ACCESS_POLICY_REVOKED',
  ACCESS_POLICY_RESOLVE = 'ACCESS_POLICY_RESOLVE',
  ACCESS_POLICY_RESOLVED = 'ACCESS_POLICY_RESOLVED',
  ACCESS_POLICY_ERROR = 'ACCESS_POLICY_ERROR',
}

// =============================================================================
// Payload schemas
// =============================================================================

/** Host → API: create a policy for a machine (or one instance on it). */
const accessPolicyCreatePayloadSchema = z.object({
  machineId: machineIdSchema,
  gameInstanceIndex: gameInstanceIndexSchema.optional(),
  maxConcurrentUsers: z.number().int().min(1),
  createdBy: z.string(),
  expiresAt: z.number().optional(),
});

/** API → Host: policy persisted; share code issued. */
const accessPolicyCreatedPayloadSchema = z.object({
  policy: accessPolicySchema,
});

/** Host → API: revoke a policy. */
const accessPolicyRevokePayloadSchema = z.object({
  policyId: z.string(),
});

/** API → Host: policy revoked. */
const accessPolicyRevokedPayloadSchema = z.object({
  policyId: z.string(),
  revoked: z.boolean(),
});

/** Player → API: resolve a share code to a session contract. */
const accessPolicyResolvePayloadSchema = z.object({
  shareCode: z.string(),
});

/**
 * API → Player: redacted policy view, host broadcast target, and the signed
 * session contract. Machine/instance identity appears ONLY inside the signed
 * contract payload (see `redactedAccessPolicySchema`).
 */
const accessPolicyResolvedPayloadSchema = z.object({
  policy: redactedAccessPolicySchema,
  broadcastId: z.string(),
  contract: signedContractSchema,
});

const accessPolicyErrorPayloadSchema = z.object({
  policyId: z.string().optional(),
  shareCode: z.string().optional(),
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

// =============================================================================
// Full message schemas
// =============================================================================

const accessPolicyCreateSchema = baseMessageSchema
  .extend({ type: z.literal(AccessPolicyMessageType.ACCESS_POLICY_CREATE) })
  .merge(accessPolicyCreatePayloadSchema);
const accessPolicyCreatedSchema = baseMessageSchema
  .extend({ type: z.literal(AccessPolicyMessageType.ACCESS_POLICY_CREATED) })
  .merge(accessPolicyCreatedPayloadSchema);
const accessPolicyRevokeSchema = baseMessageSchema
  .extend({ type: z.literal(AccessPolicyMessageType.ACCESS_POLICY_REVOKE) })
  .merge(accessPolicyRevokePayloadSchema);
const accessPolicyRevokedSchema = baseMessageSchema
  .extend({ type: z.literal(AccessPolicyMessageType.ACCESS_POLICY_REVOKED) })
  .merge(accessPolicyRevokedPayloadSchema);
const accessPolicyResolveSchema = baseMessageSchema
  .extend({ type: z.literal(AccessPolicyMessageType.ACCESS_POLICY_RESOLVE) })
  .merge(accessPolicyResolvePayloadSchema);
const accessPolicyResolvedSchema = baseMessageSchema
  .extend({ type: z.literal(AccessPolicyMessageType.ACCESS_POLICY_RESOLVED) })
  .merge(accessPolicyResolvedPayloadSchema);
const accessPolicyErrorSchema = baseMessageSchema
  .extend({ type: z.literal(AccessPolicyMessageType.ACCESS_POLICY_ERROR) })
  .merge(accessPolicyErrorPayloadSchema);

export const accessPolicyMessageSchema = z.discriminatedUnion('type', [
  accessPolicyCreateSchema,
  accessPolicyCreatedSchema,
  accessPolicyRevokeSchema,
  accessPolicyRevokedSchema,
  accessPolicyResolveSchema,
  accessPolicyResolvedSchema,
  accessPolicyErrorSchema,
]);

// =============================================================================
// Inferred types
// =============================================================================

export type AccessPolicyCreatePayload = z.infer<typeof accessPolicyCreatePayloadSchema>;
export type AccessPolicyCreatedPayload = z.infer<typeof accessPolicyCreatedPayloadSchema>;
export type AccessPolicyRevokePayload = z.infer<typeof accessPolicyRevokePayloadSchema>;
export type AccessPolicyRevokedPayload = z.infer<typeof accessPolicyRevokedPayloadSchema>;
export type AccessPolicyResolvePayload = z.infer<typeof accessPolicyResolvePayloadSchema>;
export type AccessPolicyResolvedPayload = z.infer<typeof accessPolicyResolvedPayloadSchema>;
export type AccessPolicyErrorPayload = z.infer<typeof accessPolicyErrorPayloadSchema>;

export type AccessPolicyMessage = z.infer<typeof accessPolicyMessageSchema>;

// PayloadType mapping
type PayloadType<T extends AccessPolicyMessageType> = T extends AccessPolicyMessageType.ACCESS_POLICY_CREATE
  ? AccessPolicyCreatePayload
  : T extends AccessPolicyMessageType.ACCESS_POLICY_CREATED
    ? AccessPolicyCreatedPayload
    : T extends AccessPolicyMessageType.ACCESS_POLICY_REVOKE
      ? AccessPolicyRevokePayload
      : T extends AccessPolicyMessageType.ACCESS_POLICY_REVOKED
        ? AccessPolicyRevokedPayload
        : T extends AccessPolicyMessageType.ACCESS_POLICY_RESOLVE
          ? AccessPolicyResolvePayload
          : T extends AccessPolicyMessageType.ACCESS_POLICY_RESOLVED
            ? AccessPolicyResolvedPayload
            : T extends AccessPolicyMessageType.ACCESS_POLICY_ERROR
              ? AccessPolicyErrorPayload
              : never;

/**
 * Generate an access policy message with the correct type and payload
 */
export function generateAccessPolicyMessage<T extends AccessPolicyMessageType>(
  type: T,
  payload: PayloadType<T>,
): AccessPolicyMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case AccessPolicyMessageType.ACCESS_POLICY_CREATE:
      return accessPolicyCreateSchema.parse({ ...base, ...payload });
    case AccessPolicyMessageType.ACCESS_POLICY_CREATED:
      return accessPolicyCreatedSchema.parse({ ...base, ...payload });
    case AccessPolicyMessageType.ACCESS_POLICY_REVOKE:
      return accessPolicyRevokeSchema.parse({ ...base, ...payload });
    case AccessPolicyMessageType.ACCESS_POLICY_REVOKED:
      return accessPolicyRevokedSchema.parse({ ...base, ...payload });
    case AccessPolicyMessageType.ACCESS_POLICY_RESOLVE:
      return accessPolicyResolveSchema.parse({ ...base, ...payload });
    case AccessPolicyMessageType.ACCESS_POLICY_RESOLVED:
      return accessPolicyResolvedSchema.parse({ ...base, ...payload });
    case AccessPolicyMessageType.ACCESS_POLICY_ERROR:
      return accessPolicyErrorSchema.parse({ ...base, ...payload });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
